import React, { useEffect, useMemo, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { Users } from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import {
  getOrCreateSocialDm,
  listSocialDmConversations,
  listSocialMutualFollows,
  sendSocialDmMessage,
} from "@/lib/socialDmApi";
import { toastError, toastSuccess } from "@/lib/toast";
import { markdownToPlain } from "@/lib/markdownPlain";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function normalizeSearchQuery(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/^@+/, "");
}

function matchesPerson(u, q) {
  if (!q) return true;
  const name = String(u.display_name || "").toLowerCase();
  const handle = String(u.username || "")
    .toLowerCase()
    .replace(/^@+/, "");
  return name.includes(q) || handle.includes(q);
}

function PersonLabel({ user }) {
  const name = user.display_name || user.username || "User";
  const handle = user.username
    ? String(user.username).replace(/^@+/, "")
    : null;
  return (
    <span className="min-w-0 flex items-baseline gap-1.5 truncate">
      <span className="font-medium truncate text-foreground">{name}</span>
      {handle ? (
        <span className="text-xs italic text-muted-foreground truncate shrink-0">
          @{handle}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Social “Send to…” picker: existing conversations + mutual follows for new 1:1.
 */
export default function SharePostToSocialDmModal({
  open,
  onOpenChange,
  post,
}) {
  const { accessToken } = useAuth();
  const [memberQuery, setMemberQuery] = useState("");
  const [mutualFollows, setMutualFollows] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [sendingTo, setSendingTo] = useState(null);

  const postId =
    post?.social_post_id ??
    post?.socialPostId ??
    post?.post_id ??
    post?.postId;
  const title = markdownToPlain(post?.title || "").trim();

  const currentUserId = useMemo(() => {
    try {
      return accessToken ? jwtDecode(accessToken)?.sub : null;
    } catch {
      return null;
    }
  }, [accessToken]);

  useEffect(() => {
    if (!open) {
      setMemberQuery("");
      setNote("");
      setSendingTo(null);
      return;
    }
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [users, convs] = await Promise.all([
          listSocialMutualFollows(accessToken, ""),
          listSocialDmConversations(accessToken),
        ]);
        if (cancelled) return;
        setMutualFollows(users || []);
        setConversations(convs || []);
      } catch (err) {
        console.error("Failed to load social share targets:", err);
        if (!cancelled) {
          setMutualFollows([]);
          setConversations([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accessToken]);

  useEffect(() => {
    if (!open || !accessToken) return;
    const q = normalizeSearchQuery(memberQuery);
    if (!q) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const users = await listSocialMutualFollows(accessToken, q);
        if (!cancelled) setMutualFollows(users || []);
      } catch (err) {
        console.error("Failed to search mutual follows:", err);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, accessToken, memberQuery]);

  const filteredConversations = useMemo(() => {
    const q = normalizeSearchQuery(memberQuery);
    return (conversations || []).filter((c) => {
      if (!q) return true;
      if (c.type === "group") {
        return (c.title || "Group").toLowerCase().includes(q);
      }
      return matchesPerson(c.peer || {}, q);
    });
  }, [conversations, memberQuery]);

  const peerIdsWithConversation = useMemo(() => {
    const ids = new Set();
    for (const c of conversations || []) {
      if (c.type === "group") continue;
      const peerId = c.peer?.user_id;
      if (peerId != null) ids.add(String(peerId));
    }
    return ids;
  }, [conversations]);

  const filteredPeople = useMemo(() => {
    const q = normalizeSearchQuery(memberQuery);
    return (mutualFollows || [])
      .filter((u) => String(u.user_id) !== String(currentUserId))
      .filter((u) => !peerIdsWithConversation.has(String(u.user_id)))
      .filter((u) => matchesPerson(u, q));
  }, [mutualFollows, memberQuery, currentUserId, peerIdsWithConversation]);

  const sendToPerson = async (user) => {
    if (!user?.user_id || !accessToken || postId == null) return;
    setSendingTo(`p-${user.user_id}`);
    try {
      const conv = await getOrCreateSocialDm(accessToken, user.user_id);
      await sendSocialDmMessage(accessToken, conv.conversation_id, {
        content: (note || "").trim(),
        social_post_id: Number(postId),
      });
      toastSuccess("Post sent");
      onOpenChange?.(false);
    } catch (err) {
      console.error("Failed to send post in social DM:", err);
      toastError(err, "Could not send post");
    } finally {
      setSendingTo(null);
    }
  };

  const sendToConversation = async (conversation) => {
    const cid = conversation?.conversation_id;
    if (!cid || !accessToken || postId == null) return;
    setSendingTo(`c-${cid}`);
    try {
      await sendSocialDmMessage(accessToken, cid, {
        content: (note || "").trim(),
        social_post_id: Number(postId),
      });
      toastSuccess("Post sent");
      onOpenChange?.(false);
    } catch (err) {
      console.error("Failed to send post to conversation:", err);
      toastError(err, "Could not send post");
    } finally {
      setSendingTo(null);
    }
  };

  const convLabel = (c) => {
    if (c.type === "group") return c.title || "Group";
    return c.peer?.display_name || c.peer?.username || "Direct message";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl bg-card text-card-foreground border-border shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Send to…</DialogTitle>
          <DialogDescription>
            Share this post in a message with someone you follow mutually.
            Search by name or @handle.
          </DialogDescription>
        </DialogHeader>
        {(title || postId != null) && (
          <p className="text-sm text-muted-foreground line-clamp-2 border border-border rounded-xl px-3 py-2 bg-muted">
            {title || `Post #${postId}`}
          </p>
        )}
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a message (optional)"
          className="rounded-xl"
        />
        <Input
          value={memberQuery}
          onChange={(e) => setMemberQuery(e.target.value)}
          placeholder="Search by name or @handle…"
          autoFocus
          className="rounded-xl"
        />
        <div className="max-h-64 overflow-y-auto rounded-xl border border-border divide-y divide-border bg-card">
          {loading ? (
            <p className="p-3 text-sm text-muted-foreground">Loading…</p>
          ) : filteredConversations.length === 0 &&
            filteredPeople.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No matches</p>
          ) : (
            <>
              {filteredConversations.map((c) => (
                <button
                  key={`c-${c.conversation_id}`}
                  type="button"
                  disabled={!!sendingTo}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent/60 disabled:opacity-50 flex items-center justify-between gap-2 transition-colors"
                  onClick={() => sendToConversation(c)}
                >
                  <span className="min-w-0 flex items-center gap-2 truncate">
                    {c.type === "group" ? (
                      <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <SocialUserAvatar
                        user={c.peer}
                        avatarUrl={c.peer?.avatar_url}
                        displayName={c.peer?.display_name}
                        className="h-8 w-8"
                        fallbackClassName="text-[10px]"
                      />
                    )}
                    {c.type === "group" ? (
                      <span className="font-medium truncate">{convLabel(c)}</span>
                    ) : (
                      <PersonLabel
                        user={{
                          display_name: convLabel(c),
                          username: c.peer?.username,
                        }}
                      />
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {sendingTo === `c-${c.conversation_id}`
                      ? "Sending…"
                      : "Send"}
                  </span>
                </button>
              ))}
              {filteredPeople.map((u) => (
                <button
                  key={`p-${u.user_id}`}
                  type="button"
                  disabled={!!sendingTo}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent/60 disabled:opacity-50 flex items-center justify-between gap-2 transition-colors"
                  onClick={() => sendToPerson(u)}
                >
                  <span className="min-w-0 flex items-center gap-2 truncate">
                    <SocialUserAvatar
                      user={u}
                      avatarUrl={u.avatar_url}
                      displayName={u.display_name}
                      className="h-8 w-8"
                      fallbackClassName="text-[10px]"
                    />
                    <PersonLabel user={u} />
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {sendingTo === `p-${u.user_id}` ? "Sending…" : "Send"}
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
